import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AuthShell } from "../components/AuthShell";

export default function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username) {
      toast.error("Please enter your username or email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { username });
      setDoneMessage(data?.message || "A password reset link has been sent. Check your inbox (and spam folder).");
      setDone(true);
      toast.success("Reset link sent — check your email");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell variant="copper">
      <div className="mt-6 mb-6">
        <Link
          to="/login"
          data-testid="back-to-login"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Back to login
        </Link>
      </div>
      <h2 className="text-2xl font-bold mb-2">Forgot Password</h2>
      <p className="text-white/60 text-sm mb-6">
        Enter your username or registered email — we'll send a reset link.
      </p>
      {done ? (
        <div
          data-testid="forgot-password-success"
          className="rounded-md bg-[#19a55c]/10 border border-[#19a55c]/30 px-4 py-3 text-[#7ddca7]"
        >
          {doneMessage}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="forgot-password-form">
          <div className="space-y-2">
            <label className="block text-base text-white/90" htmlFor="fp-username">
              Username or email
            </label>
            <input
              id="fp-username"
              data-testid="fp-username-input"
              type="text"
              placeholder="Your username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-12 rounded-md px-4 bg-[#1a2030] text-white placeholder:text-white/35 border border-white/5 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/30 transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="fp-submit-btn"
            className="w-full h-12 rounded-md bg-[#19a55c] hover:bg-[#21c46e] active:bg-[#168f4f] text-white font-bold text-lg transition-colors flex items-center justify-center disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={20} /> : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
